import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 🔗 Wiki - Seção Webhooks
 * 
 * Documentação completa sobre o sistema de webhooks.
 * Explica como configurar webhooks para receber notificações de eventos.
 * Voltada para desenvolvedores e usuários técnicos.
 */
const WebhooksWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:webhook" />
          <span>Webhooks</span>
        </div>
        <p className={styles.heroText}>
          Configure webhooks para receber notificações automáticas de eventos importantes 
          da plataforma. Integre sua aplicação e mantenha-se sempre atualizado.
        </p>
      </section>

      {/* O que são Webhooks */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que são Webhooks?</span>
          <p className={styles.sectionSubtitle}>
            Notificações automáticas de eventos
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              <strong>Webhooks</strong> são URLs que você configura para receber notificações 
              automáticas quando eventos importantes acontecem na plataforma. Ao invés de 
              ficar consultando a API constantemente, a plataforma <strong>envia os dados 
              diretamente para sua aplicação</strong>.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              Exemplos: quando uma venda é criada, quando um afiliado é aprovado, quando 
              um saque é processado, etc. Você recebe uma <strong>requisição HTTP POST</strong> 
              com os dados do evento.
            </p>
          </div>
        </div>
      </section>

      {/* Como Criar um Webhook */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Criar um Webhook?</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo para configurar
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:plus-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Acesse a Página de Webhooks
                </span>
                <p>
                  No <strong>menu lateral</strong>, acesse <strong>Operações → Webhooks</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:link-variant" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Configure a URL
                </span>
                <p>
                  Informe a <strong>URL do seu servidor</strong> que receberá as notificações. 
                  Deve ser uma URL pública e acessível via HTTPS.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:bell-ring" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Selecione os Eventos
                </span>
                <p>
                  Escolha quais <strong>eventos você quer receber</strong>: vendas criadas, 
                  afiliados aprovados, saques processados, etc. Você pode selecionar múltiplos eventos.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Salve o Webhook
                </span>
                <p>
                  Clique em <strong>"Criar Webhook"</strong> ou <strong>"Salvar"</strong>. 
                  O webhook será testado automaticamente e ficará ativo.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Eventos Disponíveis */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Eventos Disponíveis</span>
          <p className={styles.sectionSubtitle}>
            Tipos de eventos que você pode receber
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:cart" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Vendas</div>
                <span className={styles.cardTitle}>Eventos de Vendas</span>
                <p>
                  <strong>sale.created:</strong> Nova venda criada<br/>
                  <strong>sale.updated:</strong> Venda atualizada<br/>
                  <strong>sale.paid:</strong> Venda paga<br/>
                  <strong>sale.cancelled:</strong> Venda cancelada
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:account-network" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Afiliados</div>
                <span className={styles.cardTitle}>Eventos de Afiliação</span>
                <p>
                  <strong>affiliate.approved:</strong> Afiliado aprovado<br/>
                  <strong>affiliate.rejected:</strong> Afiliado rejeitado<br/>
                  <strong>affiliate.created:</strong> Nova solicitação de afiliação
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:cash-multiple" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Saques</div>
                <span className={styles.cardTitle}>Eventos de Saques</span>
                <p>
                  <strong>withdrawal.requested:</strong> Saque solicitado<br/>
                  <strong>withdrawal.approved:</strong> Saque aprovado<br/>
                  <strong>withdrawal.rejected:</strong> Saque rejeitado<br/>
                  <strong>withdrawal.processed:</strong> Saque processado
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:package-variant" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Produtos</div>
                <span className={styles.cardTitle}>Eventos de Produtos</span>
                <p>
                  <strong>product.created:</strong> Produto criado<br/>
                  <strong>product.updated:</strong> Produto atualizado<br/>
                  <strong>product.deleted:</strong> Produto deletado<br/>
                  <strong>product.approved:</strong> Produto aprovado
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Funciona */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Funciona?</span>
          <p className={styles.sectionSubtitle}>
            Fluxo de notificações webhook
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              Quando um evento acontece na plataforma:
            </p>
            <ol style={{ marginTop: '12px', marginBottom: 0, paddingLeft: '20px' }}>
              <li>A plataforma <strong>detecta o evento</strong> (ex: nova venda criada)</li>
              <li>Verifica quais <strong>webhooks estão configurados</strong> para esse evento</li>
              <li>Envia uma <strong>requisição HTTP POST</strong> para cada URL configurada</li>
              <li>Seu servidor <strong>recebe os dados</strong> do evento em formato JSON</li>
              <li>Você processa os dados e <strong>executa ações</strong> na sua aplicação</li>
            </ol>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              <strong>Importante:</strong> Seu servidor deve responder com status <strong>200 OK</strong> 
              para confirmar que recebeu a notificação. Caso contrário, a plataforma tentará reenviar.
            </p>
          </div>
        </div>
      </section>

      {/* Dicas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas e Boas Práticas</span>
          <p className={styles.sectionSubtitle}>
            Como usar webhooks de forma eficiente
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:shield-check" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Use HTTPS
                </span>
                <p>
                  Sempre use <strong>URLs HTTPS</strong> para garantir a segurança dos dados. 
                  URLs HTTP não são aceitas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:clock-fast" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Responda Rapidamente
                </span>
                <p>
                  Seu servidor deve <strong>responder em até 5 segundos</strong>. Respostas 
                  lentas podem causar retentativas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:filter" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Selecione Apenas Eventos Necessários
                </span>
                <p>
                  Configure apenas os eventos que você <strong>realmente precisa</strong>. 
                  Menos eventos = menos requisições = melhor performance.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:test-tube" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Teste Antes de Ativar
                </span>
                <p>
                  Sempre <strong>teste seu webhook</strong> antes de ativá-lo em produção. 
                  Use ferramentas como ngrok para testar localmente.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default WebhooksWiki;

